import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import axios from "axios";
import VisionSpaceDetail from "../components/VisionSpaceDetail";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 毛玻璃效果的卡片 - 企业化设计（可点击）
const GlassCard = styled(Card)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  cursor: "pointer",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 16,
    pointerEvents: "none",
  },
  "&:hover": {
    boxShadow: "0 10px 36px rgba(0, 0, 0, 0.25)",
    borderColor: "rgba(255, 255, 255, 0.25)",
  },
}));

// 毛玻璃效果的对话框
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "24px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    maxWidth: "800px",
    width: "90%",
  },
}));

// 状态标签 - 透明毛玻璃
const StatusChip = styled(Chip)(({ theme, status }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: "rgba(255, 255, 255, 0.95)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "8px",
  fontSize: "0.85rem",
  height: "28px",
  fontWeight: 500,
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
}));

// 作者信息容器
const AuthorContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: "auto",
  padding: theme.spacing(1, 0),
}));

// 卡片内容
const StyledCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  position: "relative",
  zIndex: 1,
}));

// 媒体类型标签 - 透明毛玻璃
const MediaTypeChip = styled(Chip)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: "rgba(255, 255, 255, 0.9)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "8px",
  fontSize: "0.85rem",
  height: "28px",
  fontWeight: 500,
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
}));

const Vision = () => {
  const location = useLocation();
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 获取活动数据
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/vision-space`);
        setActivities(response.data);
      } catch (err) {
        console.error("获取活动数据失败:", err);
        setError("获取活动数据失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  // 处理URL hash，滚动到对应的section
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // 移除#号
      const sectionId = decodeURIComponent(hash.substring(1));
      // 等待数据加载完成后再滚动
      if (activities.length > 0) {
        // 使用requestAnimationFrame确保DOM完全渲染后再滚动
        const scrollToSection = () => {
          requestAnimationFrame(() => {
            const element = document.getElementById(sectionId);
            if (element) {
              // 使用更精确的偏移量计算
              const navbarHeight = 80; // 导航栏高度
              const yOffset = -navbarHeight - 20; // 额外20px间距
              const elementTop = element.getBoundingClientRect().top;
              const elementPosition = elementTop + window.pageYOffset;
              const offsetPosition = elementPosition + yOffset;

              // 使用平滑滚动，添加更长的滚动时间
              window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
              });
            }
          });
        };

        // 等待页面完全加载和渲染
        setTimeout(scrollToSection, 150);
        // 备用方案：如果第一次没找到，再试一次
        setTimeout(scrollToSection, 600);
      }
    }
  }, [activities, location.hash]);

  // 获取所有唯一的类型，并确保清晨灵修事工在最前面
  const getUniqueTypes = () => {
    const types = activities.map((activity) => activity.type);
    const uniqueTypes = [...new Set(types)];

    // 将清晨灵修事工移到最前面
    const sortedTypes = uniqueTypes.sort((a, b) => {
      if (a === "清晨灵修事工") return -1;
      if (b === "清晨灵修事工") return 1;
      return a.localeCompare(b); // 其他类型按字母顺序排序
    });

    return sortedTypes;
  };

  // 处理卡片点击
  const handleCardClick = (activity) => {
    setSelectedActivity(activity);
    setOpenDialog(true);
  };

  // 处理关闭对话框
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedActivity(null);
  };

  // 渲染活动卡片
  const renderActivityCard = (activity) => {
    const isUpcoming = activity.status === "upcoming";
    const date = new Date(activity.date);
    const now = new Date();
    const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    return (
      <Grid item xs={12} sm={6} md={4} key={activity._id}>
        <GlassCard onClick={() => handleCardClick(activity)}>
          <StyledCardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <StatusChip
                status={activity.status}
                label={isUpcoming ? `还有 ${daysUntil} 天` : "已结束"}
              />
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255, 255, 255, 0.8)",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                }}
              >
                <AccessTimeIcon
                  sx={{
                    fontSize: 16,
                    mr: 0.5,
                    color: "rgba(255, 255, 255, 0.8)",
                  }}
                />
                {date.toLocaleDateString()}
              </Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "rgba(255, 255, 255, 0.95)",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              }}
            >
              {activity.title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.5,
                color: "rgba(255, 255, 255, 0.85)",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              }}
            >
              {activity.description}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <MediaTypeChip
                label={
                  activity.mediaType === "video"
                    ? "视频"
                    : activity.mediaType === "audio"
                    ? "音频"
                    : "无媒体"
                }
              />
              {activity.type && <MediaTypeChip label={activity.type} />}
            </Box>

            <AuthorContainer>
              {activity.authors?.map((author) => (
                <Box
                  key={author._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "2px 8px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "6px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 500,
                      fontSize: "0.85rem",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {author.name || "未知作者"}
                  </Typography>
                </Box>
              ))}
            </AuthorContainer>
          </StyledCardContent>
        </GlassCard>
      </Grid>
    );
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: { xs: 4, md: 6 },
        pb: { xs: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 },
        scrollBehavior: "smooth", // 确保整个容器使用平滑滚动
      }}
    >
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h4"
          sx={{
            color: "rgba(255, 255, 255, 0.95)",
            fontWeight: 600,
            mb: { xs: 3, md: 4 },
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
            lineHeight: 1.3,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          异象空间
        </Typography>
      </Box>

      {getUniqueTypes().map((type, index) => (
        <Box key={type} id={type} sx={{ mb: 6, scrollMarginTop: "100px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              pb: 2,
              borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "36px",
                height: "36px",
                borderRadius: "8px",
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: "0.9rem",
                fontWeight: 600,
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              {index + 1}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 500,
                color: "rgba(255, 255, 255, 0.9)",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                flex: 1,
              }}
            >
              {type}
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {activities
              .filter((activity) => activity.type === type)
              .map(renderActivityCard)}
          </Grid>
        </Box>
      ))}

      <VisionSpaceDetail
        open={openDialog}
        onClose={handleCloseDialog}
        activity={selectedActivity ? { _id: selectedActivity._id } : null}
      />
    </Container>
  );
};

export default Vision;
